import { z } from 'zod';
import { isValidSlug } from './slug';

/**
 * Schema for a project's `mogzo.json` (PRD section 5.5). The CLI validates it before
 * upload, and the review pipeline reads it to configure Game Services.
 */

/** Ids of leaderboards, achievements, post types and jobs: lowercase, digits, `-` or `_`. */
const IDENTIFIER_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const SEMVER_PATTERN = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;
const HOSTNAME_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

const identifier = z
  .string()
  .max(64)
  .regex(IDENTIFIER_PATTERN, 'Use lowercase letters, digits, - or _');

/** A file inside the uploaded bundle: relative, forward slashes, no `..`. */
const bundlePath = z
  .string()
  .min(1)
  .refine((value) => !value.startsWith('/') && !value.includes('\\'), 'Use a relative path with /')
  .refine((value) => !value.split('/').includes('..'), 'Path must stay inside the bundle');

const leaderboardSchema = z
  .strictObject({
    id: identifier,
    name: z.string().min(1).max(60).optional(),
    sort: z.enum(['asc', 'desc']).default('desc'),
    /** How scores are shown: a plain number, or a time in milliseconds (1:02.345). */
    format: z.enum(['number', 'time']).default('number'),
    reset: z.enum(['none', 'daily', 'weekly', 'monthly']).default('none'),
    min: z.number().optional(),
    max: z.number().optional(),
  })
  .refine((board) => board.min === undefined || board.max === undefined || board.min < board.max, {
    message: 'min must be less than max',
    path: ['max'],
  });

const achievementSchema = z.strictObject({
  id: identifier,
  name: z.string().min(1).max(60),
  description: z.string().max(200).optional(),
  icon: bundlePath,
  points: z.int().min(0).max(100).default(10),
  hidden: z.boolean().default(false),
  /** Number of progress steps; omit for one-shot achievements. */
  steps: z.int().min(2).max(1_000_000).optional(),
});

const postTypeSchema = z.strictObject({
  id: identifier,
  splashTemplate: identifier,
  dataSchema: bundlePath.optional(),
});

const jobSchema = z.strictObject({
  id: identifier,
  on: z.string().min(1),
  action: z.string().min(1),
});

/** Which way a game is played; the player is fitted to it. */
export const ORIENTATIONS = ['landscape', 'portrait', 'any'] as const;
export type Orientation = (typeof ORIENTATIONS)[number];

function uniqueIds<T extends { id: string }>(label: string) {
  return (items: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<string>();
    items.forEach((item, index) => {
      if (seen.has(item.id)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate ${label} id "${item.id}"`,
          path: [index, 'id'],
        });
      }
      seen.add(item.id);
    });
  };
}

export const mogzoManifestSchema = z
  .strictObject({
    id: z.string().refine(isValidSlug, 'Use 3-32 lowercase letters, digits and single hyphens'),
    /** Optional: the dashboard's project name is the game's name; this is only a fallback. */
    name: z.string().min(1).max(60).optional(),
    version: z.string().regex(SEMVER_PATTERN, 'Use a semantic version such as 1.2.0'),
    entry: bundlePath.refine((value) => value.endsWith('.html'), 'Entry must be an .html file'),
    /**
     * The game's splash (M18c): its own page shown in posts before anyone plays. Its Play calls
     * `mogzo.launch()`. Without one, MOGZO shows a default splash.
     */
    splash: bundlePath
      .refine((value) => value.endsWith('.html'), 'The splash must be an .html file')
      .optional(),
    orientation: z.enum(ORIENTATIONS).default('any'),
    devices: z
      .array(z.enum(['desktop', 'mobile']))
      .min(1)
      .default(['desktop']),
    /**
     * No longer used: leaderboards and achievements are made in the developer dashboard (decided
     * Oct 26, 2026). Still accepted so older files validate; the CLI says to remove them.
     */
    leaderboards: z.array(leaderboardSchema).superRefine(uniqueIds('leaderboard')).default([]),
    achievements: z.array(achievementSchema).superRefine(uniqueIds('achievement')).default([]),
    postTypes: z.array(postTypeSchema).superRefine(uniqueIds('post type')).default([]),
    jobs: z.array(jobSchema).superRefine(uniqueIds('job')).default([]),
    network: z
      .strictObject({
        allow: z
          .array(z.string().regex(HOSTNAME_PATTERN, 'Use a bare lowercase hostname'))
          .default([]),
      })
      .default({ allow: [] }),
    /**
     * For games with a build step (`mogzo build`): the command to run, and the folder it writes.
     * Without it, the game's folder is uploaded as it is.
     */
    build: z
      .strictObject({
        command: z.string().min(1).max(200).optional(),
        output: bundlePath.optional(),
      })
      .optional(),
  })
  .transform((manifest) => ({ ...manifest, name: manifest.name ?? manifest.id }));

/** A manifest after validation, with defaults filled in. */
export type MogzoManifest = z.output<typeof mogzoManifestSchema>;
export type LeaderboardDefinition = MogzoManifest['leaderboards'][number];
/** Leaderboard, achievement and other ids from mogzo.json. */
export const manifestIdSchema = identifier;
/** A manifest as written in `mogzo.json`. */
export type MogzoManifestInput = z.input<typeof mogzoManifestSchema>;
